import { formatAsLongDate } from "../core/helpers/date-formatting-util.js";
import Model from "./Model.js";
import TemplateExpander from "./TemplateExpander.js";

/**
 * Report disclaimers are displayed when printing and exporting reports. They
 * are rendered as a full introduction page, both on prints and pdf export.
 *
 * @param {string} [spec.heading] - Heading of the disclaimer page.
 * @param {string} [spec.content] - Content of the disclaimer page.
 * @param {string} [spec.footer] - Footer of the disclaimer page.
 */
export default class ReportDisclaimer extends Model {
	constructor({ header = "", content = "", footer = "" } = {}) {
		super(...arguments);

		this._header = header;
		this._content = content;
		this._footer = footer;
	}

	getHeader() {
		return this._header;
	}

	setHeader(header) {
		this._header = header;
	}

	getContent() {
		return this._content;
	}

	setContent(content) {
		this._content = content;
	}

	getFooter() {
		return this._footer;
	}

	setFooter(footer) {
		this._footer = footer;
	}

	/**
	 * We consider a disclaimer to be empty if its content is empty, regardless
	 * of its header and footer.
	 */
	isEmpty() {
		return this._content.length === 0;
	}

	expandHeader(variables = {}) {
		return this._expand(this._header, variables);
	}

	expandContent(variables = {}) {
		return this._expand(this._content, variables);
	}

	expandFooter(variables = {}) {
		return this._expand(this._footer, variables);
	}

	getHeaderAsParagraphs(variables = {}) {
		return this.expandHeader(variables).split("\n");
	}

	getContentAsParagraphs(variables = {}) {
		return this.expandContent(variables).split("\n");
	}

	getFooterAsParagraphs(variables = {}) {
		return this.expandFooter(variables).split("\n");
	}

	_expand(content, variables) {
		return new TemplateExpander().expand(content, variables);
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.content = this._content;
		properties.header = this._header;
		properties.footer = this._footer;

		return properties;
	}

	static buildTemplateVariables({
		agency,
		company,
		currentPeriod,
		date = new Date(),
	}) {
		return {
			agency: agency.name,
			date: formatAsLongDate(date),
			company: company.getName(),
			auditor: company.getAuditor().getName(),
			auditorTitle: company.getAuditor().getTitle(),
			auditorCity: company.getAuditor().getCity(),
			organizationNumber: company.getAuditor().getOrganizationNumber(),
			registrationNumber: company.getAuditor().getRegistrationNumber(),
			currentPeriod: currentPeriod.toSmartString(),
		};
	}
}
